import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { SectionTitle } from "@/components/dashboard/widgets";
import { MessagesSquare, Bot, Webhook, Cpu, ArrowRight, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Addons catalogue (iter78). Single home for all platform extras buyers can opt
 * into. Each card is informational + one-click for the togglable addons.
 *
 * Premium navy-led aesthetic, no fear/loss messaging. Just clear value.
 */
const STATIC_ADDONS = [
  {
    id: "auto-followup",
    Icon: MessagesSquare,
    name: "Auto Follow-Up",
    tag: "SMS + Email",
    price: "£0.05 per accepted lead",
    pricing_note: "Bundles SMS + email",
    description: "Every newly accepted lead receives a templated SMS and email from your team within 60 seconds. Increases pickup rates, reduces returns.",
    points: [
      "Merge-tag templates per sector",
      "Daily + monthly spend caps",
      "PECR/GDPR compliant — STOP opt-out auto-included",
    ],
    toggle_endpoint: "/client/followup",     // GET reads `enabled`, PUT sets it
    settings_route: "/dashboard/auto-followup",
  },
  {
    id: "reply-triage",
    Icon: Bot,
    name: "AI Reply Triage",
    tag: "Claude Sonnet 4.5",
    price: "£0.01 suggested · £0.03 auto-action",
    pricing_note: "Per processed reply",
    description: "When a lead replies to your follow-up, Claude classifies the intent (hot / reschedule / disqualify / question / unsubscribe), extracts structured data, and either suggests or executes the right action.",
    points: [
      "10 pre-built sector qualification templates",
      "Per-category auto-action toggles + confidence threshold",
      "Full audit trail; 6h grace window on auto-returns",
    ],
    settings_route: "/dashboard/auto-followup#triage",
    no_toggle: true, // engaged via the Qualification profile in Auto Follow-Up page
  },
  {
    id: "replies-inbox",
    Icon: Bot,
    name: "Replies inbox",
    tag: "Single view",
    price: "Included",
    description: "Unified inbox for every triaged reply — approve a suggestion in 1 click or override an auto-action. Filter by intent, status, confidence.",
    points: ["Channel-agnostic (SMS + email)", "Real-time updates as leads reply"],
    settings_route: "/dashboard/replies",
    no_toggle: true,
  },
  {
    id: "ai-systems",
    Icon: Cpu,
    name: "AI Add-on Systems",
    tag: "Per-system pricing",
    price: "Starts from £49/mo",
    description: "Plug-in AI operational systems — voice agents, lead scoring, AI BDR. Order as a managed product, white-glove setup.",
    points: ["Voice qualification (UK accents)", "Lead-scoring engine", "AI BDR sequences"],
    settings_route: "/dashboard/ai-systems",
    no_toggle: true,
  },
  {
    id: "webhook-delivery",
    Icon: Webhook,
    name: "Webhook delivery",
    tag: "Real-time CRM",
    price: "Included",
    description: "Signed HMAC-SHA256 webhooks deliver every accepted lead straight to your CRM. Auto-retry with exponential backoff. Connectors pre-built for HubSpot, Salesforce, Pipedrive, Zapier.",
    points: ["Signed payloads (whsec_*)", "5-attempt retry queue", "Per-lead delivery audit"],
    settings_route: "/dashboard/delivery",
    no_toggle: true,
  },
];

export default function AddonsCatalogue() {
  const nav = useNavigate();
  const [followupEnabled, setFollowupEnabled] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/client/followup").then((r) => setFollowupEnabled(!!r.data?.settings?.enabled)).catch(() => setFollowupEnabled(false));
  }, []);

  const toggleFollowup = async (next) => {
    setBusy("auto-followup");
    try {
      await api.put("/client/followup", { enabled: next });
      setFollowupEnabled(next);
      toast.success(next ? "Auto Follow-Up enabled" : "Auto Follow-Up disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <SectionTitle
        eyebrow="Platform · addons"
        title="Add-ons"
        subtitle="Optional extras that compound the value of your exclusive leads. Each one is metered or fixed — no hidden fees."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {STATIC_ADDONS.map((a) => {
          const isFollowup = a.id === "auto-followup";
          const enabled = isFollowup ? followupEnabled : null;
          return (
            <Card key={a.id} className="rounded-xl border-slate-200 shadow-soft overflow-hidden relative" data-testid={`addon-card-${a.id}`}>
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${enabled === true ? "bg-emerald-500" : "bg-[#0F172A]"}`} />
              <CardContent className="p-5 pl-6 flex flex-col h-full">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="inline-grid place-items-center size-10 rounded-md bg-[#0F172A] text-white shrink-0">
                      <a.Icon className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-[15px] font-semibold text-slate-900">{a.name}</h3>
                        {enabled === true && (
                          <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 rounded text-emerald-700 bg-emerald-500/10 border border-emerald-200">
                            <span className="size-1.5 rounded-full bg-emerald-500" /> Active
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-medium">{a.tag}</div>
                    </div>
                  </div>
                  {!a.no_toggle && enabled !== null && (
                    <Switch
                      checked={enabled}
                      onCheckedChange={(v) => toggleFollowup(v)}
                      disabled={busy === a.id}
                      data-testid={`addon-toggle-${a.id}`}
                    />
                  )}
                </div>

                <p className="mt-3 text-[12.5px] text-slate-600 leading-relaxed">{a.description}</p>

                <ul className="mt-3 space-y-1">
                  {a.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700">
                      <ShieldCheck className="size-3 text-slate-400 shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.1em] text-slate-500 font-semibold">Price</div>
                    <div className="text-[13.5px] text-slate-900 font-semibold tabular-nums mt-0.5">{a.price}</div>
                    {a.pricing_note && <div className="text-[10.5px] text-slate-500">{a.pricing_note}</div>}
                  </div>
                  <Button
                    variant={enabled === true ? "outline" : "default"}
                    className={enabled === true
                      ? "rounded-md border-slate-200 text-slate-700 hover:text-slate-900 h-9 text-[12.5px]"
                      : "rounded-md bg-[#0F172A] hover:bg-[#1e293b] text-white h-9 text-[12.5px]"}
                    onClick={() => nav(a.settings_route)}
                    data-testid={`addon-cta-${a.id}`}
                  >
                    {enabled === true ? "Manage" : "Set up"} <ArrowRight className="size-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {busy && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500"><Loader2 className="size-3.5 animate-spin" /> Updating…</div>
      )}
    </div>
  );
}
